import { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { LancFilterPanel } from './LancFilterPanel';
import type { LancFilters } from './types';

interface LancFilterProps {
  filters: LancFilters;
  onChange: (f: LancFilters) => void;
  cats: string[];
}

/**
 * Botão ícone SlidersHorizontal + badge de contagem de filtros ativos.
 * Abre LancFilterPanel (dropdown 264px à direita).
 * Fecha ao clicar fora via mousedown listener.
 * Touch target: 44×44px.
 */
export function LancFilter({ filters, onChange, cats }: LancFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeCount =
    (filters.tipo !== 'todos' ? 1 : 0) +
    (filters.cat !== 'todas' ? 1 : 0) +
    (filters.periodo !== 'mes' ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Filtros${activeCount > 0 ? ` (${activeCount} ativo${activeCount > 1 ? 's' : ''})` : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        style={{
          width: 44,
          height: 44,
          borderRadius: 'var(--r-3)',
          background: activeCount ? 'var(--mint-soft)' : 'var(--card-soft)',
          color: activeCount ? 'var(--mint-deep)' : 'var(--fg-muted)',
          border: activeCount
            ? '1px solid var(--mint-deep)'
            : '1px solid var(--card-stroke)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <SlidersHorizontal size={17} strokeWidth={1.7} aria-hidden />
        {activeCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 17,
              height: 17,
              padding: '0 4px',
              borderRadius: 'var(--r-pill)',
              background: 'var(--mint-deep)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <LancFilterPanel
          filters={filters}
          onChange={(f) => {
            onChange(f);
          }}
          cats={cats}
        />
      )}
    </div>
  );
}
